import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Youtube, Music2, Mic, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import Seo from "@/components/Seo";
import { VideoEmbed } from "@/components/VideoEmbed";

/**
 * /transmissions — hub vidéo (YouTube + TikTok).
 *
 * Grille filtrée, chaque vignette = miniature + titre + durée + source.
 * Objectif SEO : maximiser le nombre de pages `/transmissions/{slug}` indexables
 * (chaque vidéo devient une porte d'entrée organique).
 */
function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

function formatScheduled(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })
             + " à "
             + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", "h");
    } catch { return ""; }
}

function isScheduledFuture(v) {
    if (!v?.scheduled_publish_at) return false;
    return new Date(v.scheduled_publish_at).getTime() > Date.now();
}

const SOURCE_LABELS = {
    youtube: { label: "YouTube", Icon: Youtube, tone: "text-red-500" },
    tiktok: { label: "TikTok", Icon: Music2, tone: "text-secondary" },
};

export default function Transmissions() {
    const [videos, setVideos] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [featured, setFeatured] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | youtube | tiktok

    useEffect(() => {
        // 3 parallel fetches: featured (hero), interviews (top strip),
        // transmissions (main grid). The dedicated category filter keeps the
        // interview section stable even as the transmissions catalogue grows.
        Promise.all([
            http.get("/videos", { params: { limit: 100, category: "transmission" } }),
            http.get("/videos", { params: { limit: 50, category: "interview" } }),
            http.get("/videos/featured").catch(() => ({ data: null })),
        ])
            .then(([a, b, c]) => {
                setVideos(a.data || []);
                setInterviews(b.data || []);
                setFeatured(c.data || null);
            })
            .catch(() => { setVideos([]); setInterviews([]); setFeatured(null); })
            .finally(() => setLoading(false));
    }, []);

    const transmissions = videos;
    const filtered = filter === "all" ? transmissions : transmissions.filter((v) => v.source === filter);

    const collectionSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Transmissions vidéo — Foundation of Light",
        description: "Transmissions de langage de lumière, méditations guidées et enseignements en vidéo.",
        url: "https://foundation-of-light.com/transmissions",
        hasPart: videos.slice(0, 20).map((v) => ({
            "@type": "VideoObject",
            name: v.title,
            description: (v.description || "").slice(0, 300),
            thumbnailUrl: v.thumbnail_url,
            uploadDate: v.published_at,
            duration: v.duration_iso,
            embedUrl: v.source === "youtube" ? `https://www.youtube.com/embed/${v.external_id}` : undefined,
            contentUrl: v.external_url,
            url: `https://foundation-of-light.com/transmissions/${v.slug}`,
        })),
    };

    return (
        <div className="min-h-screen" data-testid="transmissions-page">
            <Seo
                title="Transmissions vidéo · Langage de lumière · Foundation of Light"
                description="Vidéos, transmissions et méditations guidées de langage de lumière : enseignements vibratoires, cercles de conscience et messages canalisés."
                path="/transmissions"
                jsonLd={collectionSchema}
            />

            <section className="max-w-6xl mx-auto px-6 pt-24 pb-10">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                    <Play className="w-4 h-4" /> Transmissions
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6">
                    Vidéos & transmissions vibratoires
                </h1>
                <p className="text-lg text-foreground/70 max-w-2xl leading-relaxed">
                    Explorez les transmissions de langage de lumière, les méditations guidées et les
                    messages canalisés d'Olivier Masurel — un espace vivant où le son, la vibration
                    et l'intention se rencontrent pour éveiller la conscience.
                </p>
            </section>

            {/* HERO · featured video (the current pinned video — can be a transmission or an interview) */}
            {featured && (
                <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="transmissions-hero">
                    <div className="rounded-3xl overflow-hidden bg-card border border-border/60 shadow-sm">
                        <div className="grid lg:grid-cols-5 gap-0">
                            <div className="lg:col-span-3">
                                <VideoEmbed video={featured} className="rounded-none lg:rounded-r-none" />
                            </div>
                            <div className="lg:col-span-2 p-7 lg:p-10 flex flex-col justify-center">
                                <div className="text-xs tracking-[0.25em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5" /> À la une
                                </div>
                                <h2 className="font-serif text-2xl sm:text-3xl leading-tight mb-4">
                                    <Link to={`/transmissions/${featured.slug}`} className="hover:text-primary transition-colors">
                                        {featured.title}
                                    </Link>
                                </h2>
                                {featured.guest_name && (
                                    <p className="text-sm text-foreground/70 flex items-center gap-1.5 mb-4">
                                        <Mic className="w-3.5 h-3.5 text-primary shrink-0" />
                                        <span>Avec <strong className="font-medium">{featured.guest_name}</strong></span>
                                    </p>
                                )}
                                {featured.description && (
                                    <p className="text-sm text-foreground/70 leading-relaxed line-clamp-4 mb-5">
                                        {featured.description.replace(/^<!-- fol:override -->\n?/, "").slice(0, 240)}
                                        {featured.description.length > 240 ? "…" : ""}
                                    </p>
                                )}
                                <Link
                                    to={`/transmissions/${featured.slug}`}
                                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                    data-testid="hero-cta"
                                >
                                    Ouvrir la transmission →
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* INTERVIEWS & CONFÉRENCES — dedicated section below hero */}
            {interviews.length > 0 && (
                <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="interviews-section">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <div className="text-sm tracking-[0.25em] uppercase text-primary mb-3 font-medium flex items-center gap-2">
                                <Mic className="w-4 h-4" /> Interviews & Conférences
                            </div>
                            <h2 className="font-serif text-3xl sm:text-4xl mb-2">Avec des intervenants extérieurs</h2>
                            <p className="text-base text-foreground/70 max-w-2xl leading-relaxed">
                                Dialogues, enseignements partagés et conférences enregistrées avec des praticien·nes et des canaux invités.
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {interviews.map((v, i) => {
                            const src = SOURCE_LABELS[v.source] || SOURCE_LABELS.youtube;
                            const Icon = src.Icon;
                            return (
                                <motion.article
                                    key={v.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                                    className="group rounded-3xl overflow-hidden bg-card border border-border/60 hover:border-primary/40 transition-colors shadow-sm hover:shadow-md flex flex-col"
                                    data-testid={`interview-card-${v.id}`}
                                >
                                    <Link to={`/transmissions/${v.slug}`} className="block relative aspect-video overflow-hidden bg-black">
                                        {v.thumbnail_url ? (
                                            <img
                                                src={v.thumbnail_url}
                                                alt={v.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-white/40">
                                                <Play className="w-12 h-12" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                        <div className={`absolute top-3 left-3 flex items-center gap-1.5 text-xs bg-black/70 text-white px-2 py-1 rounded-full ${src.tone}`}>
                                            <Icon className="w-3 h-3" /> {src.label}
                                        </div>
                                        {v.duration_human && (
                                            <div className="absolute bottom-3 right-3 text-xs bg-black/70 text-white px-2 py-0.5 rounded font-mono">
                                                {v.duration_human}
                                            </div>
                                        )}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                                                <Play className="w-8 h-8 text-primary translate-x-0.5" fill="currentColor" />
                                            </div>
                                        </div>
                                    </Link>
                                    <div className="p-5 flex flex-col flex-1">
                                        <h3 className="font-serif text-lg leading-snug mb-2 line-clamp-2">
                                            <Link to={`/transmissions/${v.slug}`} className="hover:text-primary transition-colors">
                                                {v.title}
                                            </Link>
                                        </h3>
                                        {v.guest_name && (
                                            <p className="text-sm text-foreground/70 flex items-center gap-1.5 mb-2">
                                                <Mic className="w-3.5 h-3.5 text-primary shrink-0" />
                                                <span>Avec <strong className="font-medium">{v.guest_name}</strong></span>
                                            </p>
                                        )}
                                        {v.guest_link && (
                                            <a
                                                href={v.guest_link}
                                                target="_blank"
                                                rel="noopener"
                                                className="text-xs text-primary hover:underline inline-flex items-center gap-1 mb-2"
                                                data-testid={`interview-guest-link-${v.id}`}
                                            >
                                                Découvrir son univers →
                                            </a>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-auto pt-3">
                                            {formatDate(v.published_at)}
                                        </p>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="max-w-6xl mx-auto px-6 pb-32" data-testid="transmissions-grid-section">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="text-sm tracking-[0.25em] uppercase text-primary mb-3 font-medium flex items-center gap-2">
                            <Play className="w-4 h-4" /> Transmissions vidéo
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl mb-2">Explorer toutes les transmissions</h2>
                        <p className="text-base text-foreground/70 max-w-2xl leading-relaxed">
                            L'ensemble des méditations guidées, messages canalisés et enseignements vibratoires de la Foundation of Light.
                        </p>
                    </div>
                    {/* Filter pills — moved next to the grid they filter */}
                    <div className="flex flex-wrap gap-2" data-testid="transmissions-filter">
                        {[
                            { key: "all", label: "Toutes" },
                            { key: "youtube", label: "YouTube" },
                            { key: "tiktok", label: "TikTok" },
                        ].map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                                    filter === f.key
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-card border border-border/60 text-muted-foreground hover:text-foreground hover:bg-accent/60"
                                }`}
                                data-testid={`filter-${f.key}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                {loading ? (
                    <p className="text-muted-foreground text-center py-16">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <p className="text-muted-foreground text-center py-16" data-testid="transmissions-empty">
                        Aucune vidéo pour le moment.
                    </p>
                ) : (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((v, i) => {
                            const src = SOURCE_LABELS[v.source] || SOURCE_LABELS.youtube;
                            const Icon = src.Icon;
                            const scheduled = isScheduledFuture(v);
                            return (
                                <motion.article
                                    key={v.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                                    className="group rounded-3xl overflow-hidden bg-card border border-border/60 hover:border-primary/40 transition-colors shadow-sm hover:shadow-md flex flex-col"
                                    data-testid={`video-card-${v.id}`}
                                >
                                    <Link to={`/transmissions/${v.slug}`} className={`block relative aspect-video overflow-hidden bg-black ${scheduled ? "pointer-events-none" : ""}`}>
                                        {v.thumbnail_url ? (
                                            <img
                                                src={v.thumbnail_url}
                                                alt={v.title}
                                                loading="lazy"
                                                className={`w-full h-full object-cover transition-transform duration-500 ${scheduled ? "opacity-60" : "group-hover:scale-105"}`}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-white/40">
                                                <Play className="w-12 h-12" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                        <div className={`absolute top-3 left-3 flex items-center gap-1.5 text-xs bg-black/70 text-white px-2 py-1 rounded-full ${src.tone}`}>
                                            <Icon className="w-3 h-3" /> {src.label}
                                        </div>
                                        {scheduled && (
                                            <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[11px] font-medium bg-primary text-primary-foreground px-2.5 py-1 rounded-full shadow-sm" data-testid={`scheduled-badge-${v.id}`}>
                                                <Sparkles className="w-3 h-3" /> Disponible le {formatScheduled(v.scheduled_publish_at)}
                                            </div>
                                        )}
                                        {!scheduled && v.duration_human && (
                                            <div className="absolute bottom-3 right-3 text-xs bg-black/70 text-white px-2 py-0.5 rounded font-mono">
                                                {v.duration_human}
                                            </div>
                                        )}
                                        {!scheduled && (
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                                                    <Play className="w-8 h-8 text-primary translate-x-0.5" fill="currentColor" />
                                                </div>
                                            </div>
                                        )}
                                    </Link>
                                    <div className="p-5 flex flex-col flex-1">
                                        <h2 className="font-serif text-lg leading-snug mb-2 line-clamp-2">
                                            {scheduled ? (
                                                <span className="text-foreground/80">{v.title}</span>
                                            ) : (
                                                <Link to={`/transmissions/${v.slug}`} className="hover:text-primary transition-colors">
                                                    {v.title}
                                                </Link>
                                            )}
                                        </h2>
                                        <p className="text-xs text-muted-foreground mt-auto pt-3">
                                            {scheduled ? `Programmée pour le ${formatScheduled(v.scheduled_publish_at)}` : formatDate(v.published_at)}
                                        </p>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
